import React, { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AuthContext } from '../App';
import { Phone, PhoneOff, Headphones, Video, X } from 'lucide-react';
import { toast } from 'sonner';

// Helper: genera una URL de Daily.co con token de meeting para el admin
// (inyecta userName automáticamente, evita que Daily pida nombre)
const buildCallUrl = async (url) => {
  if (!url) return url;
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    const origin = (typeof window !== 'undefined' && window.location?.origin) || '';
    const resp = await fetch(`${origin}/api/chat/video-join-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ room_url: url })
    });
    if (!resp.ok) return url;
    const data = await resp.json();
    return data.url || url;
  } catch (_e) {
    return url;
  }
};

// Widget global: modal con timbre cuando llega una llamada de soporte remota.
// Solo corre si el usuario actual es admin. Si se acepta, abre un iframe global
// con la sala de Daily.co (no depende de StreamChatComponent estar montado).
const SoporteIncomingCall = () => {
  const { API, user } = useContext(AuthContext);
  const [llamada, setLlamada] = useState(null);
  const [respondida, setRespondida] = useState(false);
  const [activeCall, setActiveCall] = useState(null); // { url, title }
  const audioCtxRef = useRef(null);
  const ringTimerRef = useRef(null);

  // Poll cada 4s para detectar llamadas de soporte pendientes
  useEffect(() => {
    if (!user || user.rol !== 'admin') return;
    let cancelled = false;

    const poll = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/soporte/llamadas-pendientes`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        const primera = data.llamadas?.[0];
        if (cancelled) return;
        if (primera && !respondida && !activeCall) {
          setLlamada((prev) => (prev?.id === primera.id ? prev : primera));
        } else if (!primera) {
          // Ya no hay llamadas pendientes (expiró o la respondieron en otro lado)
          setLlamada(null);
        }
      } catch (_e) {}
    };

    poll();
    const interval = setInterval(poll, 4000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [API, user, respondida, activeCall]);

  // Timbre mientras el modal esté visible
  useEffect(() => {
    if (!llamada) {
      if (ringTimerRef.current) { clearInterval(ringTimerRef.current); ringTimerRef.current = null; }
      try { audioCtxRef.current?.close?.(); } catch (_e) {}
      audioCtxRef.current = null;
      return;
    }
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      audioCtxRef.current = ctx;

      const playBeep = () => {
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
        [0, 400].forEach((delay) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.value = 880;
          const start = ctx.currentTime + delay / 1000;
          gain.gain.setValueAtTime(0, start);
          gain.gain.linearRampToValueAtTime(0.3, start + 0.02);
          gain.gain.linearRampToValueAtTime(0, start + 0.25);
          osc.start(start);
          osc.stop(start + 0.3);
        });
      };

      playBeep();
      ringTimerRef.current = setInterval(playBeep, 1600);
    } catch (_e) {}

    return () => {
      if (ringTimerRef.current) { clearInterval(ringTimerRef.current); ringTimerRef.current = null; }
      try { audioCtxRef.current?.close?.(); } catch (_e) {}
      audioCtxRef.current = null;
    };
  }, [llamada?.id]); // eslint-disable-line

  const responder = async (accion) => {
    if (!llamada) return;
    setRespondida(true);
    const llamadaActual = llamada;
    setLlamada(null);

    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/soporte/llamadas-pendientes/${llamadaActual.id}/${accion}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (_e) {}

    if (accion === 'aceptar') {
      const finalUrl = await buildCallUrl(llamadaActual.sala_url);
      setActiveCall({
        url: finalUrl,
        title: `Soporte · ${llamadaActual.caller_name}`
      });
      toast.success(`Conectando con ${llamadaActual.caller_name}...`);
    } else {
      toast.info('Llamada de soporte rechazada');
    }
    // Permitir que el poll vuelva a buscar nuevas llamadas tras 5s
    setTimeout(() => setRespondida(false), 5000);
  };

  const endActiveCall = () => {
    setActiveCall(null);
  };

  // Si no hay ni llamada entrante ni llamada activa, no renderizar nada
  if (!llamada && !activeCall) return null;

  return (
    <>
      {/* Modal de llamada entrante (timbre) */}
      {llamada && createPortal(
        <div
          className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-md flex items-center justify-center p-4"
          data-testid="soporte-incoming-call"
        >
          <div className="bg-gradient-to-br from-red-600 via-red-500 to-orange-500 rounded-3xl shadow-2xl p-8 max-w-md w-full text-white animate-pulse">
            <div className="text-center mb-6">
              <div className="w-24 h-24 mx-auto rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4 ring-4 ring-white/40 ring-offset-4 ring-offset-transparent animate-bounce">
                <Headphones className="w-12 h-12 text-white" />
              </div>
              <div className="inline-block px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm text-xs font-bold mb-2 tracking-wide">
                SOPORTE REMOTO
              </div>
              <h2 className="text-2xl font-bold mb-1" data-testid="soporte-caller-name">
                {llamada.caller_name}
              </h2>
              <p className="text-white/90 text-sm capitalize">{llamada.caller_rol || 'Usuario'}</p>
              <p className="text-white/70 text-xs mt-2">Solicita asistencia por videollamada</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => responder('rechazar')}
                className="py-4 bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-2xl font-semibold shadow transition-all active:scale-95 flex items-center justify-center gap-2"
                data-testid="soporte-reject-btn"
              >
                <PhoneOff className="w-5 h-5" />
                Rechazar
              </button>
              <button
                onClick={() => responder('aceptar')}
                className="py-4 bg-green-500 hover:bg-green-600 rounded-2xl font-bold shadow-lg transition-all active:scale-95 flex items-center justify-center gap-2"
                data-testid="soporte-accept-btn"
              >
                <Phone className="w-5 h-5" />
                Atender
              </button>
            </div>

            <p className="text-center text-white/60 text-xs mt-5">
              La llamada expira en ~2 minutos
            </p>
          </div>
        </div>,
        document.body
      )}

      {/* Iframe global de llamada activa (después de aceptar) */}
      {activeCall && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black flex flex-col"
          data-testid="soporte-active-call-panel"
        >
          <div className="flex items-center justify-between px-3 py-2 bg-gray-900 shrink-0">
            <div className="flex items-center gap-2 text-white min-w-0">
              <Video className="w-4 h-4 shrink-0" />
              <span className="text-sm font-medium truncate">{activeCall.title}</span>
            </div>
            <button
              onClick={endActiveCall}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors shrink-0 ml-2"
              data-testid="soporte-end-call-btn"
            >
              <X className="w-4 h-4" />
              <span className="hidden sm:inline">Finalizar</span>
              <span className="sm:hidden">Salir</span>
            </button>
          </div>
          <iframe
            src={activeCall.url}
            className="flex-1 w-full border-0"
            allow="camera *; microphone *; fullscreen; display-capture; autoplay; clipboard-write; picture-in-picture"
            allowFullScreen
            data-testid="soporte-active-call-iframe"
            title={activeCall.title}
          />
        </div>,
        document.body
      )}
    </>
  );
};

export default SoporteIncomingCall;
